/**
 * How a renter uses Reserlo.
 *
 * One definition, read by the storefront pages, the storefront API, the
 * dashboard navigation and the central operator panel, so a menu item, a page,
 * a server action and an API route can never disagree about whether a store
 * has a public shop.
 *
 * - `storefront` — the Reserlo shop is the renter's rental website.
 * - `external-website` — the renter keeps their own website and links into
 *   the same Reserlo shop for the catalogue and the booking.
 * - `dashboard-only` — no public shop at all; the renter enters reservations
 *   by hand and handles the rental themselves.
 *
 * `storefront` is the default and the value an existing store keeps until the
 * mode is changed on purpose: a store that never heard of this setting must
 * behave exactly as it did before.
 */
export const USAGE_MODES = ["storefront", "external-website", "dashboard-only"] as const;

export type UsageMode = (typeof USAGE_MODES)[number];

export const DEFAULT_USAGE_MODE: UsageMode = "storefront";

export function isUsageMode(value: unknown): value is UsageMode {
  return typeof value === "string" && (USAGE_MODES as readonly string[]).includes(value);
}

/**
 * The mode a store actually runs in. Anything unset, unknown or corrupted
 * falls back to the default, because the alternative — guessing
 * `dashboard-only` — would silently close a shop that is open today.
 */
export function resolveUsageMode(value: unknown): UsageMode {
  return isUsageMode(value) ? value : DEFAULT_USAGE_MODE;
}

/** What a mode allows. Every surface derives its behaviour from this. */
export interface UsageModeCapabilities {
  /** Browsable public shop: home, catalogue, product pages, cart, checkout, embed. */
  publicStorefront: boolean;
  /** Public reservation requests may be created by a visitor. */
  publicRequests: boolean;
  /** The storefront links back to a website the renter runs themselves. */
  externalWebsiteLink: boolean;
  /** The store is listed in its own sitemap and allowed in robots.txt. */
  searchIndexable: boolean;
}

const CAPABILITIES: Record<UsageMode, UsageModeCapabilities> = {
  storefront: {
    publicStorefront: true,
    publicRequests: true,
    externalWebsiteLink: false,
    searchIndexable: true,
  },
  "external-website": {
    publicStorefront: true,
    publicRequests: true,
    externalWebsiteLink: true,
    searchIndexable: true,
  },
  "dashboard-only": {
    publicStorefront: false,
    publicRequests: false,
    externalWebsiteLink: false,
    searchIndexable: false,
  },
};

export function usageModeCapabilities(mode: UsageMode): UsageModeCapabilities {
  return CAPABILITIES[mode];
}

/**
 * Storefront paths that stay reachable when the public shop is closed.
 *
 * These are links a renter hands to one named customer, or a legal page: the
 * token link from a reservation e-mail, the customer's own account area with
 * their contract, invoices and inspection reports, a payment or deposit link
 * for one reservation, the review invitation, and the terms. Closing the shop
 * must not take a signed contract away from the customer who received it.
 *
 * Everything not listed here is closed, so a route added later is closed until
 * someone decides otherwise.
 */
export const STOREFRONT_PATHS_OPEN_WITHOUT_SHOP = [
  "/r",
  "/account",
  "/pay",
  "/authorize-deposit",
  "/review",
  "/terms",
  "/legal",
] as const;

/**
 * Whether a storefront path survives a closed shop.
 *
 * `path` is the public path as the proxy saw it (`/catalog`, `/r/abc?token=1`),
 * never the internal `/{slug}/...` rewrite. A missing path is treated as
 * closed: a storefront request that did not come through the proxy has no
 * business reaching a page that the mode says does not exist.
 */
export function isStorefrontPathOpenWithoutShop(path: string | null | undefined): boolean {
  if (!path) return false;
  const pathname = path.split("?")[0].replace(/\/+$/, "") || "/";
  return STOREFRONT_PATHS_OPEN_WITHOUT_SHOP.some(
    (open) => pathname === open || pathname.startsWith(`${open}/`),
  );
}
